import { useMemo, useState } from 'react';
import {
  FaCheckCircle,
  FaChevronDown,
  FaChevronRight,
  FaDownload,
  FaExclamationTriangle,
  FaTimesCircle,
} from 'react-icons/fa';
import { Badge } from '../../../components/Badge/Badge';
import { cn } from '../../../utils/cn';
import { Button } from '../../PolicyAdmin/components/Button';
import { Card, PageHeader, SectionCard } from '../../PolicyAdmin/components/primitives';
import { formatCurrency, formatDateTime, formatNumber } from '../../PolicyAdmin/utils';
import { bordereauFileName, bordereauToCsv, buildBordereau, formatCell } from '../bordereau';
import { FilterBar } from '../components/FilterBar';
import { BORDEREAU_TYPE_META, CURRENCY_OPTIONS, PREVIEW_ROW_LIMIT } from '../constants';
import { useReportingStore } from '../reportingStore';
import type { BordereauType } from '../types';
import { downloadCsv } from '../utils';

const FIELD_STYLE: React.CSSProperties = {
  backgroundColor: 'var(--color-background-default)',
  borderColor: 'var(--color-border-light)',
  color: 'var(--color-text-primary)',
};

const SettingField: React.FC<{
  label: string;
  hint?: string;
  children: React.ReactNode;
}> = ({ label, hint, children }) => (
  <label className="flex flex-col gap-1">
    <span
      className="text-xs font-semibold uppercase tracking-wide"
      style={{ color: 'var(--color-text-secondary)' }}
    >
      {label}
    </span>
    {children}
    {hint && (
      <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
        {hint}
      </span>
    )}
  </label>
);

export const BordereauxPage: React.FC = () => {
  const records = useReportingStore(s => s.records);
  const loading = useReportingStore(s => s.loading);
  const filters = useReportingStore(s => s.filters);
  const bordereauType = useReportingStore(s => s.bordereauType);
  const settings = useReportingStore(s => s.bordereauSettings);
  const setBordereauType = useReportingStore(s => s.setBordereauType);
  const setSettings = useReportingStore(s => s.setBordereauSettings);
  const exportHistory = useReportingStore(s => s.exportHistory);
  const recordExport = useReportingStore(s => s.recordExport);

  const [showColumns, setShowColumns] = useState(false);

  const build = useMemo(
    () => buildBordereau(records, settings, bordereauType),
    [records, settings, bordereauType]
  );

  const errors = build.issues.filter(issue => issue.severity === 'error');
  const warnings = build.issues.filter(issue => issue.severity === 'warning');
  const previewRows = build.rows.slice(0, PREVIEW_ROW_LIMIT);

  const download = () => {
    const fileName = bordereauFileName(bordereauType, settings);
    downloadCsv(fileName, bordereauToCsv(build, settings.includeBom));
    recordExport({
      fileName,
      bordereauType,
      rowCount: build.totals.rowCount,
      grossPremium: build.totals.grossPremium,
      periodLabel: `${filters.fromDate} → ${filters.toDate}`,
      hadWarnings: warnings.length > 0,
    });
  };

  const canDownload = build.rows.length > 0 && !loading;

  return (
    <div>
      <PageHeader
        eyebrow="Reporting"
        title="Bordereaux"
        subtitle="Generate a Lloyd's coverholder risk and premium bordereau from the filtered book."
        actions={
          <Button
            leftIcon={<FaDownload className="h-3 w-3" />}
            onClick={download}
            disabled={!canDownload || errors.length > 0}
          >
            Download CSV
          </Button>
        }
      />

      <FilterBar />

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="min-w-0 xl:col-span-2">
          <SectionCard
            title="Bordereau type"
            description="Risk and premium are usually submitted together for a binding authority."
            className="mb-6"
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {(Object.keys(BORDEREAU_TYPE_META) as BordereauType[]).map(type => {
                const meta = BORDEREAU_TYPE_META[type];
                const isActive = bordereauType === type;
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setBordereauType(type)}
                    aria-pressed={isActive}
                    className={cn(
                      'rounded-lg border p-3 text-left transition-colors',
                      !isActive && 'hover:bg-[var(--color-hover-background)]'
                    )}
                    style={{
                      borderColor: isActive
                        ? 'var(--color-primary-500)'
                        : 'var(--color-border-light)',
                      backgroundColor: isActive ? 'var(--color-primary-50)' : undefined,
                    }}
                  >
                    <span
                      className="block text-sm font-semibold"
                      style={{ color: 'var(--color-text-primary)' }}
                    >
                      {meta.label}
                    </span>
                    <span
                      className="mt-1 block text-xs"
                      style={{ color: 'var(--color-text-secondary)' }}
                    >
                      {meta.description}
                    </span>
                  </button>
                );
              })}
            </div>
          </SectionCard>

          <SectionCard
            title="Binding authority details"
            description="Contract-level values repeated on every row. Confirm these against the slip before submitting."
            className="mb-6"
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <SettingField label="Unique Market Reference (UMR)">
                <input
                  value={settings.umr}
                  onChange={e => setSettings({ umr: e.target.value.toUpperCase() })}
                  className="rounded-lg border px-3 py-2 text-sm"
                  style={FIELD_STYLE}
                />
              </SettingField>
              <SettingField label="Broker reference">
                <input
                  value={settings.brokerReference}
                  onChange={e => setSettings({ brokerReference: e.target.value })}
                  className="rounded-lg border px-3 py-2 text-sm"
                  style={FIELD_STYLE}
                />
              </SettingField>
              <SettingField label="Coverholder name">
                <input
                  value={settings.coverholderName}
                  onChange={e => setSettings({ coverholderName: e.target.value })}
                  className="rounded-lg border px-3 py-2 text-sm"
                  style={FIELD_STYLE}
                />
              </SettingField>
              <SettingField label="Coverholder PIN">
                <input
                  value={settings.coverholderPin}
                  onChange={e => setSettings({ coverholderPin: e.target.value })}
                  className="rounded-lg border px-3 py-2 text-sm"
                  style={FIELD_STYLE}
                />
              </SettingField>
              <SettingField label="Binding authority reference">
                <input
                  value={settings.binderReference}
                  onChange={e => setSettings({ binderReference: e.target.value })}
                  className="rounded-lg border px-3 py-2 text-sm"
                  style={FIELD_STYLE}
                />
              </SettingField>
              <SettingField label="Year of account">
                <input
                  type="number"
                  value={settings.yearOfAccount}
                  onChange={e => setSettings({ yearOfAccount: Number(e.target.value) })}
                  className="rounded-lg border px-3 py-2 text-sm"
                  style={FIELD_STYLE}
                />
              </SettingField>
              <SettingField
                label="Lloyd's risk code"
                hint="Per the binder — overrides the product default."
              >
                <input
                  value={settings.riskCode}
                  onChange={e => setSettings({ riskCode: e.target.value.toUpperCase() })}
                  className="rounded-lg border px-3 py-2 text-sm"
                  style={FIELD_STYLE}
                />
              </SettingField>
              <SettingField label="Class of business">
                <input
                  value={settings.classOfBusiness}
                  onChange={e => setSettings({ classOfBusiness: e.target.value })}
                  className="rounded-lg border px-3 py-2 text-sm"
                  style={FIELD_STYLE}
                />
              </SettingField>
              <SettingField label="Section number">
                <input
                  value={settings.section}
                  onChange={e => setSettings({ section: e.target.value })}
                  className="rounded-lg border px-3 py-2 text-sm"
                  style={FIELD_STYLE}
                />
              </SettingField>
              <SettingField label="Reporting period end">
                <input
                  type="date"
                  value={settings.periodEnd}
                  onChange={e => setSettings({ periodEnd: e.target.value })}
                  className="rounded-lg border px-3 py-2 text-sm"
                  style={FIELD_STYLE}
                />
              </SettingField>
              <SettingField label="Original currency">
                <select
                  value={settings.originalCurrency}
                  onChange={e => setSettings({ originalCurrency: e.target.value })}
                  className="rounded-lg border px-3 py-2 text-sm"
                  style={FIELD_STYLE}
                >
                  {CURRENCY_OPTIONS.map(code => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
                </select>
              </SettingField>
              <SettingField label="Settlement currency">
                <select
                  value={settings.settlementCurrency}
                  onChange={e => setSettings({ settlementCurrency: e.target.value })}
                  className="rounded-lg border px-3 py-2 text-sm"
                  style={FIELD_STYLE}
                >
                  {CURRENCY_OPTIONS.map(code => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
                </select>
              </SettingField>
              <SettingField
                label="Rate of exchange"
                hint={
                  settings.originalCurrency === settings.settlementCurrency
                    ? 'Currencies match, so this should stay at 1.'
                    : `${settings.originalCurrency} to ${settings.settlementCurrency}.`
                }
              >
                <input
                  type="number"
                  step="0.0001"
                  min="0"
                  value={settings.rateOfExchange}
                  onChange={e => setSettings({ rateOfExchange: Number(e.target.value) })}
                  className="rounded-lg border px-3 py-2 text-sm"
                  style={FIELD_STYLE}
                />
              </SettingField>
              <label className="flex items-start gap-2.5 pt-6">
                <input
                  type="checkbox"
                  checked={settings.includeBom}
                  onChange={e => setSettings({ includeBom: e.target.checked })}
                  className="mt-0.5 h-4 w-4 accent-[var(--color-primary-500)]"
                />
                <span className="text-sm" style={{ color: 'var(--color-text-primary)' }}>
                  Add a UTF-8 byte order mark
                  <span className="block text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                    Keeps accented insured names intact when the file is opened in Excel.
                  </span>
                </span>
              </label>
            </div>
          </SectionCard>

          <SectionCard
            title="Validation"
            description="Checks applied before submission: mandatory fields, formats and internal consistency."
            className="mb-6"
            bodyClassName={build.issues.length ? 'p-0' : undefined}
            actions={
              <div className="flex items-center gap-2">
                <Badge variant={errors.length ? 'error' : 'success'} size="sm">
                  {errors.length} {errors.length === 1 ? 'error' : 'errors'}
                </Badge>
                <Badge variant={warnings.length ? 'warning' : 'secondary'} size="sm">
                  {warnings.length} {warnings.length === 1 ? 'warning' : 'warnings'}
                </Badge>
              </div>
            }
          >
            {build.issues.length === 0 ? (
              <div className="flex items-center gap-3">
                <FaCheckCircle
                  className="h-5 w-5 flex-shrink-0"
                  style={{ color: 'var(--color-success-500)' }}
                />
                <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                  {build.rows.length
                    ? 'Every row passes. The file is ready to send to the broker.'
                    : 'No rows to validate — adjust the filters to include some transactions.'}
                </p>
              </div>
            ) : (
              <ul>
                {build.issues.map(issue => (
                  <li
                    key={`${issue.severity}-${issue.columnKey}-${issue.message}`}
                    className="flex items-start gap-3 border-b px-5 py-3 last:border-b-0"
                    style={{ borderColor: 'var(--color-border-light)' }}
                  >
                    {issue.severity === 'error' ? (
                      <FaTimesCircle
                        className="mt-0.5 h-4 w-4 flex-shrink-0"
                        style={{ color: 'var(--color-error-500)' }}
                      />
                    ) : (
                      <FaExclamationTriangle
                        className="mt-0.5 h-4 w-4 flex-shrink-0"
                        style={{ color: 'var(--color-warning-500)' }}
                      />
                    )}
                    <div className="min-w-0">
                      <p
                        className="text-sm font-semibold"
                        style={{ color: 'var(--color-text-primary)' }}
                      >
                        {issue.columnHeader}
                      </p>
                      <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                        {issue.message}
                      </p>
                      <p
                        className="mt-0.5 text-xs"
                        style={{ color: 'var(--color-text-secondary)' }}
                      >
                        {formatNumber(issue.affectedCount)} affected · {issue.sampleRows.join(', ')}
                        {issue.affectedCount > issue.sampleRows.length ? ' …' : ''}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>

          <SectionCard
            title="Preview"
            description={
              build.rows.length > PREVIEW_ROW_LIMIT
                ? `First ${PREVIEW_ROW_LIMIT} of ${formatNumber(build.rows.length)} rows. The CSV contains all of them.`
                : `${formatNumber(build.rows.length)} ${build.rows.length === 1 ? 'row' : 'rows'}, exactly as they will be written.`
            }
            bodyClassName="p-0"
          >
            {build.rows.length === 0 ? (
              <p className="p-5 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                No transactions in the selected period.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-xs">
                  <thead>
                    <tr>
                      {build.columns.map(column => (
                        <th
                          key={column.key}
                          className="whitespace-nowrap px-3 py-2 text-left font-semibold uppercase tracking-wide"
                          style={{
                            color: 'var(--color-text-secondary)',
                            borderBottom: '1px solid var(--color-border-light)',
                          }}
                        >
                          {column.header}
                          {column.required && (
                            <span
                              style={{ color: 'var(--color-error-500)' }}
                              title="Mandatory field"
                            >
                              {' '}
                              *
                            </span>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((row, index) => (
                      <tr
                        key={String(row.certificateReference ?? index)}
                        style={{ borderBottom: '1px solid var(--color-border-light)' }}
                      >
                        {build.columns.map(column => (
                          <td
                            key={column.key}
                            className="whitespace-nowrap px-3 py-2 tabular-nums"
                            style={{ color: 'var(--color-text-primary)' }}
                          >
                            {formatCell(row[column.key], column.format) || (
                              <span style={{ color: 'var(--color-text-secondary)' }}>—</span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>
        </div>

        <div className="min-w-0">
          <Card className="mb-6">
            <h3 className="mb-3 text-sm font-bold" style={{ color: 'var(--color-text-primary)' }}>
              Submission summary
            </h3>
            <dl className="space-y-2 text-sm">
              {[
                ['Rows', formatNumber(build.totals.rowCount)],
                ['Columns', formatNumber(build.columns.length)],
                ['Gross premium', formatCurrency(build.totals.grossPremium)],
                ['Net premium', formatCurrency(build.totals.netPremium)],
                ['Tax & fees', formatCurrency(build.totals.taxesAndFees)],
                ['Period', `${filters.fromDate} → ${filters.toDate}`],
              ].map(([label, value]) => (
                <div key={label} className="flex items-baseline justify-between gap-3">
                  <dt style={{ color: 'var(--color-text-secondary)' }}>{label}</dt>
                  <dd
                    className="tabular-nums font-semibold"
                    style={{ color: 'var(--color-text-primary)' }}
                  >
                    {value}
                  </dd>
                </div>
              ))}
            </dl>

            <div className="mt-4 space-y-2">
              <Button
                fullWidth
                leftIcon={<FaDownload className="h-3 w-3" />}
                onClick={download}
                disabled={!canDownload || errors.length > 0}
              >
                Download CSV
              </Button>
              {errors.length > 0 && (
                <>
                  <p className="text-xs" style={{ color: 'var(--color-error-600)' }}>
                    Resolve {errors.length} {errors.length === 1 ? 'error' : 'errors'} before
                    submitting — Lloyd&apos;s will reject the file as it stands.
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    fullWidth
                    onClick={download}
                    disabled={!canDownload}
                  >
                    Download anyway
                  </Button>
                </>
              )}
            </div>
          </Card>

          <Card className="mb-6" padded={false}>
            <button
              type="button"
              onClick={() => setShowColumns(prev => !prev)}
              aria-expanded={showColumns}
              className="flex w-full items-center justify-between gap-2 px-5 py-4 text-left"
            >
              <span>
                <span
                  className="block text-sm font-bold"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  Column reference
                </span>
                <span className="block text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                  {build.columns.length} columns · {build.columns.filter(c => c.required).length}{' '}
                  mandatory
                </span>
              </span>
              {showColumns ? (
                <FaChevronDown
                  className="h-3 w-3 flex-shrink-0"
                  style={{ color: 'var(--color-text-secondary)' }}
                />
              ) : (
                <FaChevronRight
                  className="h-3 w-3 flex-shrink-0"
                  style={{ color: 'var(--color-text-secondary)' }}
                />
              )}
            </button>
            {showColumns && (
              <ul className="border-t" style={{ borderColor: 'var(--color-border-light)' }}>
                {build.columns.map(column => (
                  <li
                    key={column.key}
                    className="border-b px-5 py-2.5 last:border-b-0"
                    style={{ borderColor: 'var(--color-border-light)' }}
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span
                        className="text-xs font-semibold"
                        style={{ color: 'var(--color-text-primary)' }}
                      >
                        {column.header}
                      </span>
                      {column.required && (
                        <span
                          className="flex-shrink-0 text-[10px] font-bold uppercase"
                          style={{ color: 'var(--color-error-500)' }}
                        >
                          Required
                        </span>
                      )}
                    </div>
                    <p className="mt-0.5 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                      {column.hint}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <SectionCard
            title="Recent exports"
            description="Generated in this session."
            bodyClassName="p-0"
          >
            {exportHistory.length === 0 ? (
              <p className="p-5 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                Nothing exported yet.
              </p>
            ) : (
              <ul>
                {exportHistory.map(entry => (
                  <li
                    key={entry.id}
                    className="border-b px-5 py-3 last:border-b-0"
                    style={{ borderColor: 'var(--color-border-light)' }}
                  >
                    <p
                      className="truncate text-xs font-semibold"
                      style={{ color: 'var(--color-text-primary)' }}
                    >
                      {entry.fileName}
                    </p>
                    <p className="mt-0.5 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                      {formatNumber(entry.rowCount)} rows · {formatCurrency(entry.grossPremium)} ·{' '}
                      {formatDateTime(entry.generatedAt)}
                    </p>
                    {entry.hadWarnings && (
                      <p className="mt-0.5 text-xs" style={{ color: 'var(--color-warning-700)' }}>
                        Exported with warnings
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>
        </div>
      </div>
    </div>
  );
};
